import { List, Paragraph } from '@zillow/constellation-10';

import {
  checkDisplayForRentByChangeWarning,
  checkIsApplicationAcceptedValueChanging,
  getRoomForRentFlow,
  type HasContentObjType,
} from '../../../utils/roomForRentUtils';

import { FlowType } from '../../CreateListingPage/types';

import type { ForRentByType } from '@zg-rentals/ts-rental-manager-api-beta-client';

type RoomForRentData = {
  currentRoommatesDetails?: {
    roommateFemaleCount?: string;
    roommateMaleCount?: string;
    roommateNonBinaryCount?: string;
    roommatePreferNotToIdentifyCount?: string;
    roommateTotalCount?: string;
    roommateDetailsDescription?: string;
  };
  roommateRequirements?: {
    roommateGenderPreferences?: Array<string>;
    roommateMinAgePreference?: number | null;
  };
};

interface ForRentByWarningProps {
  roomForRentFlow: string | null;
  nextForRentByVal: ForRentByType;
  roomForRent: RoomForRentData;
  isApplicationsAccepted: boolean;
}

const hasTrueValueInObj = (obj: HasContentObjType): boolean => {
  return Object.values(obj).some((value) => value === true);
};

const ForRentByWarning = ({
  roomForRentFlow,
  nextForRentByVal,
  roomForRent,
  isApplicationsAccepted,
}: ForRentByWarningProps): JSX.Element => {
  const { hasContentObj } = checkDisplayForRentByChangeWarning({
    roomForRentFlow,
    roomForRent,
  });

  const { roommateTotalCount, roommateCount, roommateDetailsDescription, renterAgeAndGenderPreferred } = hasContentObj;

  const { isApplicationAcceptedValueChanging, isApplicationsAcceptedChangingToTrueValue } =
    checkIsApplicationAcceptedValueChanging({
      isApplicationsAccepted,
      nextForRentByVal,
    });

  const isFlowChanging = roomForRentFlow !== getRoomForRentFlow(nextForRentByVal);
  const hasContentThatWillBeDeleted = isFlowChanging && hasTrueValueInObj(hasContentObj);

  const displayIfPreviouslyEnteredInformation = hasContentThatWillBeDeleted;

  return (
    <div data-testid="ForRentByWarning">
      {displayIfPreviouslyEnteredInformation && (
        <Paragraph css={{ marginBottom: '400' }}>
          If you previously entered the following information, it will be deleted when you change who’s listing:
        </Paragraph>
      )}

      {isFlowChanging && roomForRentFlow === FlowType.ROOM_FOR_RENT_IRENE && (
        <List.Root>{roommateTotalCount && <List.Item>Roommate count</List.Item>}</List.Root>
      )}

      {isFlowChanging && roomForRentFlow === FlowType.ROOM_FOR_RENT_RYAN && (
        <List.Root>
          {roommateCount && <List.Item>Roommate count</List.Item>}
          {roommateDetailsDescription && <List.Item>Roommates description</List.Item>}
          {renterAgeAndGenderPreferred && <List.Item>Renter age and gender preferred</List.Item>}
        </List.Root>
      )}

      {isApplicationAcceptedValueChanging && (
        <>
          {isApplicationsAcceptedChangingToTrueValue ? (
            <Paragraph css={{ marginBottom: '400' }}>
              You’ll be automatically opted into our applications feature. Opt out at any time.
            </Paragraph>
          ) : (
            <Paragraph>
              You’ll be automatically opted out of our applications feature. Use of Zillow Applications is not currently
              available for tenants.
            </Paragraph>
          )}
        </>
      )}
    </div>
  );
};

export default ForRentByWarning;
